import { Button as AntButton, Modal, Table, TableColumnsType } from 'antd';
import _ from 'lodash';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getEmployees } from '../../../../api/employees.api';
import { createEventRegister } from '../../../../api/events.api';
import useAxios from '../../../../hooks/useAxios';
import useNotification from '../../../../hooks/useNotification';
import config from '../../../../lib/config';
import {
  IEmployeeItem,
  IGetEmployees,
} from '../../../../models/employees.model';
import { RootState } from '../../../../store/rootReducer';
import Button from '../../../Button';
import Checkbox from '../../../Checkbox';
import ErrorResponse from '../../../ErrorResponse';
import Loader from '../../../Loader';
import Pagination from '../../../Pagination';

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  eventNo: number | undefined;
  doGetEventRegister: () => void;
}

const SelectEmployeeModal: FC<IProps> = ({
  visible,
  setVisible,
  eventNo,
  doGetEventRegister,
}) => {
  const { t } = useTranslation();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetEmployees>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();

  const [data, setData] = useState<IEmployeeItem[]>([]);
  const [page, setPage] = useState(1);
  const [memberName, setMemberName] = useState('');
  const [selects, setSelects] = useState<string[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);

  useEffect(() => {
    if (currentCampus) {
      execute(getEmployees({ campusCode: currentCampus }));
    }
  }, [currentCampus]);

  useEffect(() => {
    if (res && !isLoading) {
      setData(res.data);
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      doGetEventRegister();
      successNotification(t('common.message.save1'));
      onClose();
    }
  }, [submitRes, submitIsLoading]);

  const onSetSelects = (memberCode: string) => {
    if (selects.includes(memberCode)) {
      setSelects(selects.filter((item) => item !== memberCode));
      setIsSelectAll(false);
    } else {
      setSelects([...selects, memberCode]);
    }
  };

  const onSelectAll = () => {
    if (isSelectAll) {
      setSelects([]);
      setIsSelectAll(false);
    } else {
      const _selects = _.uniq(data.map((item) => item.memberCode));
      setSelects(_selects);
      setIsSelectAll(true);
    }
  };

  const onSearch = () => {
    let _data = _.cloneDeep(res?.data);

    if (_data) {
      if (memberName) {
        _data = _data.filter(
          (item) =>
            item.memberName?.includes(memberName) ||
            item.memberEngName?.includes(memberName),
        );
      }
      setData(_data);
    }
  };

  const onSubmit = () => {
    if (!eventNo) return;
    if (selects.length === 0) {
      return infoNotification(t('event.education.message11'));
    }

    const req: any = [];
    selects.forEach((item) => {
      req.push({
        campusCode: currentCampus,
        memberCode: item,
      });
    });

    submitExecute(createEventRegister('ED', eventNo, req));
  };

  const onClose = () => {
    setSelects([]);
    setIsSelectAll(false);
    setVisible(false);
  };

  const columns: TableColumnsType<IEmployeeItem> = [
    {
      title: t('event.education.column22'),
      dataIndex: 'memberName',
      key: 'memberName',
      sorter: (a, b) => a.memberName?.localeCompare(b.memberName),
      align: 'center',
      width: '40%',
    },
    {
      title: 'ID',
      dataIndex: 'webId',
      key: 'webId',
      sorter: (a, b) => a.webId?.localeCompare(b.webId),
      align: 'center',
      width: '40%',
    },
    {
      title: (
        <Checkbox
          checked={isSelectAll}
          onClickCapture={(e: any) => {
            e.stopPropagation();
            onSelectAll();
          }}
          text=""
        />
      ),
      dataIndex: 'memberCode',
      key: 'memberCode',
      align: 'center',
      sorter: (a: any, b: any) => {
        let num1 = 0;
        let num2 = 0;
        if (selects.includes(a.campusCode)) num1 = 1;
        if (selects.includes(b.campusCode)) num2 = 1;

        return num1 - num2;
      },
      render: (value) => {
        let checked = false;
        if (selects.includes(value)) checked = true;

        return (
          <div className="text-center">
            <Checkbox
              checked={checked}
              onChange={() => onSetSelects(value)}
              text=""
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      {isLoading || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />

      <Modal
        title={t('event.education.title9')}
        visible={visible}
        onCancel={onClose}
        footer={[
          <div className='comm-btn__area justify-content-center'>	
            <AntButton key="submit" className='btn-basic pink' onClick={onSubmit}>
              {t('event.education.button8')}
            </AntButton>
            <AntButton key="cancel" className='btn-basic gray' onClick={onClose}>
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        <div className="form-group flex">
          <div className="w-100 mr-1">
            <input
              type="text"
              className="form-control"
              placeholder={t('event.education.placeholder3')}
              value={memberName}
              onChange={(e) => setMemberName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
          </div>
          <div className="w-100p">
            <Button
              className="btn btn-secondary btn-w100p"
              onClick={onSearch}
              text={t('common.button.search')}
            />
          </div>
        </div>
        <div>
          <Table
            className="comm-table__area"
            columns={columns}
            dataSource={data}
            pagination={{
              current: page,
              pageSize: config.miniPageSize,
            }}
            rowKey="memberCode"
          />
        </div>
        <div className="common-paging center">
          <Pagination
            count={data?.length || 0}
            page={page}
            setPage={setPage}
            mini
          />
        </div>
      </Modal>
    </>
  );
};

export default SelectEmployeeModal;
